import React from 'react';

interface Props {
  /**
   * Ein bestätigtes Konto pflegt dieses Profil. Kommt vom Server (`PublicOrganizer.verified`) —
   * Clients rechnen das nie selbst aus.
   */
  verified?: boolean;
  /** Kompakte Form ohne Text — für Listen und Karten. */
  compact?: boolean;
}

const CHECK_PATH = 'M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 '
  + '013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 '
  + '3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 '
  + '4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 '
  + '012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 '
  + '6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 '
  + '001.14-.094l3.75-5.25z';

const Check: React.FC<{ size: number }> = ({ size }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: size, height: size, flexShrink: 0 }}>
    <path fillRule="evenodd" d={CHECK_PATH} clipRule="evenodd" />
  </svg>
);

/**
 * Kennzeichnet ein Profil, das der Veranstalter selbst pflegt.
 *
 * **Wortwahl ist hier keine Geschmacksfrage.** Der Haken sagt etwas über die *Identität* aus
 * („dieses Profil gehört wirklich diesem Veranstalter"), nicht über *Qualität*. „Geprüft",
 * „Top", „Premium" oder „Empfohlen" wären eine Bewertung. Aus demselben Grund darf er Reihenfolge
 * und Filter nicht beeinflussen: täte er das, wäre er eine Platzierung und bräuchte die
 * Kennzeichnung „Anzeige".
 *
 * **Er kostet seit dem 25.08.2026 nichts mehr.** Er hängt an der Verknüpfung des Kontos
 * (`organizer_members`), nicht an einer Buchung — und deshalb steht im Hinweistext auch kein Wort
 * mehr über Kosten. Eine gekaufte Identitätsaussage ist genau das, was der Haken nicht sein soll:
 * wer sein Profil selbst pflegt, steht dahinter, ob er zahlt oder nicht.
 *
 * Ohne Haken wird **nichts** gezeigt — kein grauer Platzhalter. Ein grauer Haken auf einer
 * öffentlichen Seite läse sich als Aussage über den Verein („nicht geprüft"), und im
 * Pflege-Zustand gäbe es ihn ohnehin nie: wer dort hereinsieht, ist der aktive Verwalter.
 */
const OrganizerVerifiedBadge: React.FC<Props> = ({ verified, compact }) => {
  if (!verified) return null;

  const title = 'Dieses Profil wird vom Veranstalter selbst gepflegt. '
    + 'Der Haken sagt nichts über die Veranstaltung aus und ändert weder Reihenfolge noch Filter.';

  return (
    <span
      title={title}
      aria-label={`Verifiziert. ${title}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: compact ? 11 : 12, fontWeight: 700,
        color: '#2563eb', whiteSpace: 'nowrap',
        border: compact ? 'none' : '1px solid #2563eb',
        borderRadius: 999, padding: compact ? 0 : '2px 8px',
      }}
    >
      <Check size={14} />
      {!compact && 'Verifiziert'}
    </span>
  );
};

export default OrganizerVerifiedBadge;
